import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Purge any legacy localStorage token from previous Bearer-based auth releases.
// Safe no-op if it isn't there.
const purgeLegacyToken = () => {
  try { localStorage.removeItem("bbm_token"); } catch (_e) { /* noop */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  // On mount, ask the server who we are — the httpOnly access_token cookie is
  // sent automatically via axios `withCredentials`. 401 simply means logged out.
  useEffect(() => {
    purgeLegacyToken();
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  // Silent session refresh: while authenticated, slide the 7-day cookie forward
  // every 24h so an active admin never gets kicked out. On 401, drop the session
  // and let the login screen prompt for creds.
  useEffect(() => {
    if (!user) return;
    const ONE_DAY = 24 * 60 * 60 * 1000;
    const id = setInterval(() => {
      api.post("/auth/refresh").catch(() => setUser(null));
    }, ONE_DAY);
    return () => clearInterval(id);
  }, [user]);

  const login = async (email, password) => {
    // Backend sets the httpOnly access_token cookie on this response.
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) { /* even if the network fails, drop local session */ }
    setUser(null);
  };

  const value = useMemo(() => ({ user, checking, login, logout }), [user, checking]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
